import { affiliateApi, brandApi } from "@/lib/axios";

/** affiliate: the Creator collaborations app. data: the Analytics & Reporting app. */
export type TikTokShopApp = "affiliate" | "data";

export interface TikTokShopAppStatus {
  /** The app is configured on the server; when false, connecting is not possible yet. */
  enabled: boolean;
  connected: boolean;
  status: "ACTIVE" | "NEEDS_RECONNECT" | "DISCONNECTED" | null;
  shopName: string | null;
  region: string | null;
  grantedScopes: string[];
  /** Scopes the app asks for today that this connection predates; reconnecting grants them. */
  missingScopes: string[];
}

/** PRE_RELEASE_TIKTOK: how far each TikTok surface is along; coming_soon renders a preview. */
export type TikTokFeatureState = "live" | "beta" | "coming_soon";
export type TikTokFeatureKey =
  | "analytics"
  | "creatorSearch"
  | "samples"
  | "adRequests"
  | "invites"
  | "messages";
export type TikTokFeatureMap = Record<TikTokFeatureKey, TikTokFeatureState>;

export interface TikTokShopStatus extends Record<TikTokShopApp, TikTokShopAppStatus> {
  /** PRE_RELEASE_TIKTOK: the admin let this brand in before the affiliate scopes were approved. */
  preRelease: boolean;
  /** PRE_RELEASE_TIKTOK: per-surface state, set on the server. */
  features: TikTokFeatureMap;
}

/** The Order Information scope; order sync needs the data app to carry it. */
export const TIKTOK_ORDER_SCOPE = "seller.order.info";

export async function getTikTokShopStatus(): Promise<TikTokShopStatus> {
  const { data } = await brandApi.get<TikTokShopStatus>("/tiktok-shop/status");
  return data;
}

/** The TikTok authorize link; send the browser there to start connecting. */
export async function getTikTokShopOauthUrl(app: TikTokShopApp = "affiliate"): Promise<{ url: string }> {
  const { data } = await brandApi.get<{ url: string }>("/tiktok-shop/oauth/url", { params: { app } });
  return data;
}

export async function disconnectTikTokShop(app: TikTokShopApp = "affiliate"): Promise<{ disconnected: boolean }> {
  const { data } = await brandApi.delete<{ disconnected: boolean }>("/tiktok-shop/connection", { params: { app } });
  return data;
}

export interface TikTokMoney {
  amount: string;
  currency: string;
}

export interface TikTokShopPerformanceInterval {
  startDate: string;
  endDate: string;
  gmv: TikTokMoney | null;
  /** GMV split by sales channel: VIDEO, LIVE or PRODUCT_CARD. */
  gmvBreakdowns: Array<{ type: string } & TikTokMoney>;
  ordersCount: number;
  itemsSold: number;
  refunds: TikTokMoney | null;
  /** Daily average unique buyers. */
  avgCustomersCount: number | null;
  avgVisitors: number | null;
  avgPageViews: number | null;
  /** Visitors-to-buyers percentage as TikTok reports it, e.g. "2.8%". */
  avgConversionRate: string | null;
}

export interface TikTokShopPerformance {
  /** Last day TikTok has data for; the series never extends past it. */
  latestAvailableDate: string | null;
  intervals: TikTokShopPerformanceInterval[];
}

export type TikTokCreatorTimeSlot = "1D" | "7D" | "30D";

export interface TikTokBestsellingCreator {
  rank: number;
  /** TikTok only discloses a GMV range, never an exact figure. */
  gmvRange: string;
  openId: string;
  username: string;
  nickname: string;
  followersCount: number;
  likesCount: number;
  /** Set when the creator matches one of the brand's affiliates' TikTok handles. */
  affiliate: { id: string; name: string } | null;
  /** From TikTok's Creator Marketplace; null until synced or when the brand lacks that scope. */
  avatarUrl: string | null;
  profileUrl: string | null;
  /** The creator's top shoppable videos for this shop in the period, by GMV (up to 3). */
  topVideos: TikTokCreatorVideo[];
}

export interface TikTokCreatorVideo {
  id: string;
  title: string;
  url: string;
  /** Null when the video is private or TikTok could not resolve a cover. */
  thumbnailUrl: string | null;
  views: number;
  gmv: TikTokMoney | null;
}

export interface TikTokBestsellingCreators {
  date: string;
  timeSlot: TikTokCreatorTimeSlot;
  authorType: string;
  creators: TikTokBestsellingCreator[];
}

export async function getTikTokShopPerformance(days: number): Promise<TikTokShopPerformance> {
  const { data } = await brandApi.get<TikTokShopPerformance>("/tiktok-shop/analytics/shop-performance", {
    params: { days },
  });
  return data;
}

export async function getTikTokBestsellingCreators(
  timeSlot: TikTokCreatorTimeSlot,
): Promise<TikTokBestsellingCreators> {
  const { data } = await brandApi.get<TikTokBestsellingCreators>("/tiktok-shop/analytics/creators/bestselling", {
    params: { time_slot: timeSlot },
  });
  return data;
}

export interface TikTokVideoPerformance {
  id: string;
  title: string;
  username: string;
  views: number;
  gmv: TikTokMoney | null;
  /** GMV per 1,000 views; TikTok's content-efficiency measure. */
  gpm: TikTokMoney | null;
  clickThroughRate: string | null;
  itemsSold: number;
  videoPostTime: number | null;
  url: string;
  /** Null when the video is private or TikTok could not resolve a cover. */
  thumbnailUrl: string | null;
}

export interface TikTokVideoPerformances {
  latestAvailableDate: string | null;
  videos: TikTokVideoPerformance[];
}

export interface TikTokHourlyDay {
  date: string;
  /** One entry per hour 0-23; null when TikTok has no data for that day yet. */
  hours: Array<{ hour: number; gmv: number; visitors: number }> | null;
}

export async function getTikTokVideoPerformance(days: number): Promise<TikTokVideoPerformances> {
  const { data } = await brandApi.get<TikTokVideoPerformances>("/tiktok-shop/analytics/videos", {
    params: { days },
  });
  return data;
}

export async function getTikTokHourlyPerformance(): Promise<{ days: TikTokHourlyDay[] }> {
  const { data } = await brandApi.get<{ days: TikTokHourlyDay[] }>(
    "/tiktok-shop/analytics/shop-performance/hourly",
  );
  return data;
}

/* ---------------------------------------------------------------------------
 * Creator Marketplace search (Social Search › TikTok creators)
 * ------------------------------------------------------------------------- */

export type TikTokGmvRange =
  | "GMV_RANGE_0_100"
  | "GMV_RANGE_100_1000"
  | "GMV_RANGE_1000_10000"
  | "GMV_RANGE_10000_AND_ABOVE";

export type TikTokUnitsSoldRange =
  | "UNITS_SOLD_RANGE_0_10"
  | "UNITS_SOLD_RANGE_10_100"
  | "UNITS_SOLD_RANGE_100_1000"
  | "UNITS_SOLD_RANGE_1000_AND_ABOVE";

export type TikTokAgeRange =
  | "AGE_RANGE_18_24"
  | "AGE_RANGE_25_34"
  | "AGE_RANGE_35_44"
  | "AGE_RANGE_45_54"
  | "AGE_RANGE_55_AND_ABOVE";

/** The brand's filters, in the flat shape the backend accepts. */
export interface TikTokCreatorSearchFilters {
  keyword?: string;
  nicheIds?: string[];
  categoryIds?: string[];
  gmvRanges?: TikTokGmvRange[];
  followersMin?: number;
  followersMax?: number;
  ageRanges?: TikTokAgeRange[];
  gender?: "MALE" | "FEMALE";
  genderMinPercent?: number;
  languageIds?: string[];
  creatorLevelIds?: string[];
  minAvgVideoViews?: number;
  minEngagementRate?: number;
  minLiveViewers?: number;
  unitsSoldRanges?: TikTokUnitsSoldRange[];
  maxCommissionRate?: 5 | 10 | 15 | 20;
  postRate?: "OK" | "GOOD" | "BETTER";
  agencyStatus?: "AGENCY_MANAGED" | "INDEPENDENT";
  fastGrowing?: boolean;
  notInvitedLast90Days?: boolean;
  sortBy?: string;
}

export interface TikTokFilterOption {
  id: string;
  name: string;
}

export interface TikTokCreatorSearchCatalog {
  market: string;
  niches: TikTokFilterOption[];
  languages: TikTokFilterOption[];
  creatorLevels: TikTokFilterOption[];
  sorters: TikTokFilterOption[];
  categories: TikTokFilterOption[];
  categoryNames: Record<string, string>;
}

export interface TikTokMoneyValue {
  amount: number;
  currency: string;
}

export interface TikTokRangeValue {
  min: number;
  max: number | null;
  label: string;
}

export interface TikTokMarketplaceCreator {
  openId: string;
  username: string;
  nickname: string | null;
  avatarUrl: string | null;
  profileUrl: string;
  region: string | null;
  categories: Array<{ id: string; name: string | null }>;
  followers: number | null;
  avgVideoViews: number | null;
  avgLiveViewers: number | null;
  gmv: TikTokMoneyValue | null;
  videoGmv: TikTokMoneyValue | null;
  liveGmv: TikTokMoneyValue | null;
  gmvRange: (TikTokRangeValue & { currency: string }) | null;
  unitsSold: TikTokRangeValue | null;
  audience: {
    ageRanges: TikTokAgeRange[];
    gender: "MALE" | "FEMALE" | null;
    genderPercent: number | null;
  };
  signals: {
    fastGrowing: boolean;
    risingStar: boolean;
    officialRecommend: boolean;
    collaborated: boolean;
    invitedLast90Days: boolean;
    agencyName: string | null;
    level: number | null;
    ppsScore: number | null;
  };
  /** The brand's own affiliate with this TikTok handle, if any. */
  affiliate: { id: string; name: string } | null;
}

export interface TikTokCreatorSearch {
  id: string;
  filters: TikTokCreatorSearchFilters;
  summary: string;
  createdAt: string;
  resultCount: number;
  hasMore: boolean;
  creators: TikTokMarketplaceCreator[];
}

export interface TikTokCreatorSearchHistoryItem {
  id: string;
  summary: string;
  resultCount: number;
  createdAt: string;
}

export async function getTikTokCreatorSearchCatalog(): Promise<TikTokCreatorSearchCatalog> {
  const { data } = await brandApi.get<TikTokCreatorSearchCatalog>("/tiktok-shop/creators/search/filters");
  return data;
}

export async function searchTikTokCreators(filters: TikTokCreatorSearchFilters): Promise<TikTokCreatorSearch> {
  const { data } = await brandApi.post<TikTokCreatorSearch>("/tiktok-shop/creators/search", filters);
  return data;
}

export async function loadMoreTikTokCreators(searchId: string): Promise<TikTokCreatorSearch> {
  const { data } = await brandApi.post<TikTokCreatorSearch>(`/tiktok-shop/creators/search/${searchId}/pages`);
  return data;
}

export async function getTikTokCreatorSearch(searchId: string): Promise<TikTokCreatorSearch> {
  const { data } = await brandApi.get<TikTokCreatorSearch>(`/tiktok-shop/creators/search/${searchId}`);
  return data;
}

export async function listTikTokCreatorSearches(): Promise<TikTokCreatorSearchHistoryItem[]> {
  const { data } = await brandApi.get<TikTokCreatorSearchHistoryItem[]>("/tiktok-shop/creators/search");
  return data;
}

export async function deleteTikTokCreatorSearch(searchId: string): Promise<void> {
  await brandApi.delete(`/tiktok-shop/creators/search/${searchId}`);
}

// ---------------------------------------------------------------- samples

export type TikTokSampleTab = "TO_REVIEW" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
export type TikTokSampleDecision = "APPROVE" | "REJECT" | "REVIEW";
export type TikTokRejectReason = "NOT_MATCH" | "OFFLINE" | "OUT_OF_STOCK" | "OTHER";

export interface TikTokSampleSuggestion {
  decision: TikTokSampleDecision;
  reasons: string[];
}

export interface TikTokSampleApplication {
  id: string;
  applicationId: string;
  /** TikTok status: PENDING, AWAITING_SHIPMENT, SHIPPED, CONTENT_PENDING, COMPLETED or a cancelled state. */
  status: string;
  tab: TikTokSampleTab;
  fulfillmentStatus: string | null;
  creator: {
    openId: string;
    username: string;
    nickname: string | null;
    avatarUrl: string | null;
    followerCount: number | null;
    /** null: the creator keeps it private. */
    gmv: number | null;
    fulfillmentRate: number | null;
    medianViews: number | null;
    contentCount: number | null;
  };
  affiliate: { id: string; name: string } | null;
  product: { id: string; title: string | null; skuId: string | null; skuName: string | null; imageUrl: string | null };
  commissionRate: string | null;
  approveExpiresAt: string | null;
  isApprovable: boolean;
  disapprovableReasons: string[];
  partnerName: string | null;
  reviewedBy: "MANUAL" | "AUTO" | null;
  reviewedAt: string | null;
  rejectReason: TikTokRejectReason | null;
  suggestion: TikTokSampleSuggestion | null;
  contentCount: number;
  createdAt: string;
}

export interface TikTokSampleContent {
  /** Reveshare row id; what an ad request points at. */
  id: string;
  contentId: string;
  format: "VIDEO" | "LIVE" | string;
  url: string | null;
  pageLink: string | null;
  description: string | null;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  paidOrderCount: number;
  postedAt: string | null;
  liveEndedAt: string | null;
  /** The brand's latest "request for ads" on this video, if any. */
  adRequest: { id: string; status: TikTokAdRequestStatus } | null;
}

export interface TikTokSampleDetail extends TikTokSampleApplication {
  contents: TikTokSampleContent[];
  contentsSyncedAt: string | null;
  autoDecision: TikTokSampleSuggestion | null;
}

export interface TikTokSampleList {
  tab: TikTokSampleTab;
  counts: Record<TikTokSampleTab, number>;
  items: TikTokSampleApplication[];
  nextCursor: string | null;
  lastSyncedAt: string | null;
}

export interface TikTokSampleSyncResult {
  skipped: boolean;
  synced: number;
  autoApproved: number;
  autoRejected: number;
  lastSyncedAt: string | null;
}

export type TikTokSamplePolicyMode = "OFF" | "SUGGEST" | "AUTO";

export interface TikTokSamplePolicy {
  mode: TikTokSamplePolicyMode;
  minFollowers: number | null;
  minGmv: number | null;
  minFulfillmentRate: number | null;
  minMedianViews: number | null;
  approveAffiliates: boolean;
  rejectBelow: boolean;
  dailyApprovalCap: number | null;
}

export interface TikTokSampleRule {
  productId: string;
  active: boolean;
  status: string | null;
  quota: number | null;
  availableQuantity: number | null;
  timeUnlimited: boolean;
  startTime: string | null;
  endTime: string | null;
  minFollowers: number | null;
  minGmv: number | null;
  minAvgViews: number | null;
  categoryIds: string[];
  predictedFulfillmentRank: string | null;
}

export interface TikTokSampleRuleInput {
  active: boolean;
  quota?: number;
  timeUnlimited?: boolean;
  startTime?: string;
  endTime?: string;
  minFollowers?: number;
  minGmv?: number;
  minAvgViews?: number;
  predictedFulfillmentRank?: "ALL" | "LOW" | "MEDIUM" | "HIGH";
}

export interface TikTokAffiliateProduct {
  id: string;
  title: string;
  imageUrl: string | null;
  status: string | null;
  inventory: number | null;
  priceMin: string | null;
  priceMax: string | null;
  currency: string | null;
  /** Open-collaboration commission in percent. */
  openCommission: number | null;
  showcaseCreatorCount: number;
  contentCreatorCount: number;
}

export async function listTikTokSamples(tab: TikTokSampleTab, cursor?: string): Promise<TikTokSampleList> {
  const { data } = await brandApi.get<TikTokSampleList>("/tiktok-shop/samples", { params: { tab, cursor } });
  return data;
}

export async function syncTikTokSamples(): Promise<TikTokSampleSyncResult> {
  const { data } = await brandApi.post<TikTokSampleSyncResult>("/tiktok-shop/samples/sync");
  return data;
}

export async function getTikTokSample(id: string): Promise<TikTokSampleDetail> {
  const { data } = await brandApi.get<TikTokSampleDetail>(`/tiktok-shop/samples/${id}`);
  return data;
}

export async function reviewTikTokSample(
  id: string,
  result: "APPROVE" | "REJECT",
  rejectReason?: TikTokRejectReason,
): Promise<TikTokSampleApplication> {
  const { data } = await brandApi.post<TikTokSampleApplication>(`/tiktok-shop/samples/${id}/review`, { result, rejectReason });
  return data;
}

export async function bulkReviewTikTokSamples(
  ids: string[],
  result: "APPROVE" | "REJECT",
  rejectReason?: TikTokRejectReason,
): Promise<Array<{ id: string; ok: boolean; code?: string; message?: string }>> {
  const { data } = await brandApi.post("/tiktok-shop/samples/review", { ids, result, rejectReason });
  return data;
}

export async function getTikTokSamplePolicy(): Promise<TikTokSamplePolicy> {
  const { data } = await brandApi.get<TikTokSamplePolicy>("/tiktok-shop/samples/policy");
  return data;
}

export async function updateTikTokSamplePolicy(policy: TikTokSamplePolicy): Promise<TikTokSamplePolicy> {
  const { data } = await brandApi.put<TikTokSamplePolicy>("/tiktok-shop/samples/policy", policy);
  return data;
}

export async function listTikTokSampleRules(): Promise<Array<{ product: TikTokAffiliateProduct; rule: TikTokSampleRule | null }>> {
  const { data } = await brandApi.get("/tiktok-shop/samples/rules");
  return data;
}

export async function saveTikTokSampleRule(productId: string, input: TikTokSampleRuleInput): Promise<TikTokSampleRule> {
  const { data } = await brandApi.put<TikTokSampleRule>(`/tiktok-shop/samples/rules/${productId}`, input);
  return data;
}

// ---------------------------------------------------------- collab invites

export type TikTokCollaborationStatus = "VALID" | "ONGOING" | "EXPIRING" | "CANCELING" | "COMPLETED";

export interface TikTokCollaboration {
  id: string;
  name: string;
  message: string | null;
  startTime: string | null;
  endTime: string | null;
  createdAt: string | null;
  invitedCount: number;
  showcaseCount: number;
  contentCount: number;
  productCount: number;
  freeSample: boolean;
  sampleApprovalExempt: boolean;
  type: string | null;
}

export interface TikTokCollaborationDetail extends TikTokCollaboration {
  sellerContact: { email: string | null; phone: string | null; whatsapp: string | null };
  products: Array<{
    id: string;
    title: string | null;
    imageUrl: string | null;
    priceMin: string | null;
    priceMax: string | null;
    currency: string | null;
    commission: number | null;
    adsCommission: number | null;
    status: string | null;
    collaborationStatus: string | null;
  }>;
  creators: Array<{
    openId: string;
    username: string;
    nickname: string | null;
    avatarUrl: string | null;
    showcaseProductCount: number;
    contentProductCount: number;
    collaborationStatus: string | null;
    affiliate: { id: string; name: string } | null;
  }>;
}

export interface TikTokOutreachQuota {
  unlimited: boolean;
  total: number | null;
  used: number | null;
  remaining: number | null;
  maxFollowerCount: number | null;
  gmvTier: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface TikTokCreatorPromotion {
  products: Array<{
    productId: string;
    title: string | null;
    imageUrl: string | null;
    added: boolean;
    videoCount: number;
    liveCount: number;
    commission: number | null;
    openCommission: number | null;
    commissionEffective: boolean;
  }>;
}

export interface TikTokCollaborationInput {
  name: string;
  message?: string;
  endTime: string;
  products: Array<{ id: string; commission: number; adsCommission?: number }>;
  creatorOpenIds: string[];
  sellerContact: { email?: string; phone?: string; whatsapp?: string };
  freeSample: boolean;
  sampleApprovalExempt: boolean;
  preferredContentType?: "VIDEO" | "LIVE";
}

export interface TikTokCollaborationCreated {
  id: string | null;
  conflicts: Array<{ creatorOpenId: string; productId: string }>;
  invalidCreatorOpenIds: string[];
  invalidProductIds: string[];
}

/** A creator picked for an invite or a message; enough to show who it is. */
export interface TikTokCreatorPick {
  openId: string;
  username: string;
  nickname?: string | null;
  avatarUrl?: string | null;
  followerCount?: number | null;
}

export async function listTikTokCollaborations(
  status: TikTokCollaborationStatus,
  keyword?: string,
  cursor?: string,
): Promise<{ items: TikTokCollaboration[]; total: number; nextCursor: string | null }> {
  const { data } = await brandApi.get("/tiktok-shop/collaborations", { params: { status, keyword: keyword || undefined, cursor } });
  return data;
}

export async function getTikTokCollaboration(id: string): Promise<TikTokCollaborationDetail> {
  const { data } = await brandApi.get<TikTokCollaborationDetail>(`/tiktok-shop/collaborations/${id}`);
  return data;
}

export async function listTikTokAffiliateProducts(): Promise<TikTokAffiliateProduct[]> {
  const { data } = await brandApi.get<TikTokAffiliateProduct[]>("/tiktok-shop/collaborations/products");
  return data;
}

export async function getTikTokOutreachQuota(): Promise<TikTokOutreachQuota> {
  const { data } = await brandApi.get<TikTokOutreachQuota>("/tiktok-shop/collaborations/quota");
  return data;
}

export async function checkTikTokCollaborationConflicts(
  creatorOpenIds: string[],
  productIds: string[],
): Promise<{ hasConflict: boolean; conflicts: Array<{ creatorOpenId: string; productId: string; existingCollaborationId: string | null }> }> {
  const { data } = await brandApi.post("/tiktok-shop/collaborations/conflicts", { creatorOpenIds, productIds });
  return data;
}

export async function createTikTokCollaboration(input: TikTokCollaborationInput): Promise<TikTokCollaborationCreated> {
  const { data } = await brandApi.post<TikTokCollaborationCreated>("/tiktok-shop/collaborations", input);
  return data;
}

export async function getTikTokCollaborationLink(id: string): Promise<{ link: string }> {
  const { data } = await brandApi.post<{ link: string }>(`/tiktok-shop/collaborations/${id}/link`);
  return data;
}

export async function getTikTokCreatorPromotion(collaborationId: string, openId: string): Promise<TikTokCreatorPromotion> {
  const { data } = await brandApi.get<TikTokCreatorPromotion>(
    `/tiktok-shop/collaborations/${collaborationId}/creators/${encodeURIComponent(openId)}`,
  );
  return data;
}

// --------------------------------------------------------------- messaging

export type TikTokConversationFilter = "ALL" | "UNREAD" | "UNREPLIED" | "STARRED" | "ARCHIVED";

export interface TikTokConversation {
  id: string;
  username: string;
  avatarUrl: string | null;
  unreadCount: number;
  creatorImId: string | null;
  affiliate: { id: string; name: string } | null;
}

export interface TikTokMessage {
  id: string;
  index: string;
  type: string;
  from: "CREATOR" | "SHOP" | "SYSTEM";
  content: {
    text?: string;
    title?: string;
    productId?: string;
    productIds?: string[];
    collaborationId?: string;
    applicationId?: string;
    imageUrl?: string;
    width?: number;
    height?: number;
  };
  createdAt: string | null;
}

export type TikTokOutgoingMessage =
  | { type: "TEXT"; text: string }
  | { type: "PRODUCT_CARD"; productId: string }
  | { type: "TARGET_COLLABORATION_CARD"; collaborationId: string }
  | { type: "FREE_SAMPLE_CARD"; applicationId: string }
  | { type: "CRM_TEXT_WITH_PRODUCTS_CARD"; productIds: string[]; title?: string; text: string };

export async function listTikTokConversations(
  filter: TikTokConversationFilter,
  cursor?: string,
): Promise<{ conversations: TikTokConversation[]; nextCursor: string | null }> {
  const { data } = await brandApi.get("/tiktok-shop/messages/conversations", { params: { filter, cursor } });
  return data;
}

export async function getTikTokInboxCounts(): Promise<Partial<Record<TikTokConversationFilter, number>>> {
  const { data } = await brandApi.get("/tiktok-shop/messages/counts");
  return data;
}

export async function openTikTokConversation(creatorOpenId: string): Promise<TikTokConversation> {
  const { data } = await brandApi.post<TikTokConversation>("/tiktok-shop/messages/conversations", { creatorOpenId });
  return data;
}

export async function listTikTokMessages(
  conversationId: string,
  creatorImId: string | null,
  cursor?: string,
): Promise<{ messages: TikTokMessage[]; nextCursor: string | null }> {
  const { data } = await brandApi.get(`/tiktok-shop/messages/conversations/${conversationId}/messages`, {
    params: { cursor, creatorImId: creatorImId ?? undefined },
  });
  return data;
}

export async function sendTikTokMessage(conversationId: string, message: TikTokOutgoingMessage): Promise<{ messageId: string | null }> {
  const { data } = await brandApi.post(`/tiktok-shop/messages/conversations/${conversationId}/messages`, message);
  return data;
}

export async function markTikTokConversationRead(conversationId: string): Promise<void> {
  await brandApi.post(`/tiktok-shop/messages/conversations/${conversationId}/read`);
}

export async function setTikTokConversationStatus(
  conversationId: string,
  action: "STARRED" | "UNSTARRED" | "ARCHIVED",
): Promise<void> {
  await brandApi.post(`/tiktok-shop/messages/conversations/${conversationId}/status`, { action });
}

/* ---------------------------------------------------------------------------
 * Stored orders (Order Information scope on the data app)
 * ------------------------------------------------------------------------- */

export type TikTokOrdersUnavailableReason = "NOT_CONNECTED" | "NEEDS_RECONNECT" | "SCOPE_MISSING" | "NOT_SYNCED";

export interface TikTokOrderSyncStatus {
  available: boolean;
  reason: TikTokOrdersUnavailableReason | null;
  provider: "TIKTOK_SHOP" | "TIKTOK_SHOP_DATA" | null;
  backfill: { from: string; complete: boolean; startedAt: string } | null;
  /** Orders updated on TikTok after this instant are not in yet. */
  syncedThrough: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  ordersSynced: number;
}

export interface TikTokDailyOrderSales extends TikTokOrderSyncStatus {
  currency: string;
  days: Array<{ date: string; sales: number; orders: number }>;
  /** TikTok orders left out because Shopify already has them as affiliate sales. */
  dedupedOrders: number;
}

export async function getTikTokOrderSyncStatus(): Promise<TikTokOrderSyncStatus> {
  const { data } = await brandApi.get<TikTokOrderSyncStatus>("/tiktok-shop/orders/status");
  return data;
}

export async function getTikTokDailyOrderSales(days: number): Promise<TikTokDailyOrderSales> {
  const { data } = await brandApi.get<TikTokDailyOrderSales>("/tiktok-shop/orders/daily", { params: { days } });
  return data;
}

export async function startTikTokOrderSync(): Promise<{ started: string; status: TikTokOrderSyncStatus }> {
  const { data } = await brandApi.post<{ started: string; status: TikTokOrderSyncStatus }>("/tiktok-shop/orders/sync");
  return data;
}

/* ---------------------------------------------------------------------------
 * Affiliate side: the affiliate's own standing on a brand's TikTok Shop
 * ------------------------------------------------------------------------- */

export interface AffiliateTikTokWindow {
  timeSlot: TikTokCreatorTimeSlot;
  date: string;
  rank: number;
  /** TikTok discloses a GMV band, never an exact figure. */
  gmvRange: string;
  topVideos: TikTokCreatorVideo[];
}

export interface AffiliateTikTokStanding {
  available: boolean;
  reason: "BRAND_NOT_CONNECTED" | "NO_HANDLE" | "ANALYTICS_UNAVAILABLE" | null;
  shopName: string | null;
  handles: string[];
  windows: Record<"7D" | "30D", AffiliateTikTokWindow | null> | null;
}

export async function getAffiliateTikTokStanding(programId: string): Promise<AffiliateTikTokStanding> {
  const { data } = await affiliateApi.get<AffiliateTikTokStanding>(`/tiktok-shop/affiliate/standing/${programId}`);
  return data;
}

// ------------------------------------------------------------ ad requests

export type TikTokAdRequestStatus =
  | "REQUESTED"
  | "ACCEPTED"
  | "SUBMITTED"
  | "APPROVED"
  | "LAUNCHED"
  | "REJECTED"
  | "CANCELLED"
  | "EXPIRED";

export type TikTokAdRequestChecklistItem = "NO_WATERMARK" | "LICENSED_AUDIO" | "VERTICAL_9_16" | "RAW_FILE";

export const AD_REQUEST_CHECKLIST: Array<{ value: TikTokAdRequestChecklistItem; label: string }> = [
  { value: "NO_WATERMARK", label: "No TikTok watermark" },
  { value: "LICENSED_AUDIO", label: "Original or licensed audio (no commercial sounds)" },
  { value: "VERTICAL_9_16", label: "Vertical 9:16, at least 1080×1920" },
  { value: "RAW_FILE", label: "The original export, not a re-download" },
];

export interface TikTokAdRequestVideo {
  id: string;
  contentId: string;
  format: string;
  url: string | null;
  pageLink: string | null;
  description: string | null;
  viewCount: number;
  likeCount: number;
  paidOrderCount: number;
  postedAt: string | null;
}

export interface TikTokAdRequest {
  id: string;
  status: TikTokAdRequestStatus;
  creator: { openId: string; username: string; nickname: string | null; avatarUrl: string | null };
  affiliate: { id: string; name: string; enrolled: boolean } | null;
  program: { id: string; title: string; commissionRate: number };
  video: TikTokAdRequestVideo;
  flatFee: number | null;
  commissionRate: number;
  checklist: TikTokAdRequestChecklistItem[];
  notes: string | null;
  /** Existing campaign / ad set the ad will run in; null means a new campaign. */
  target: { campaignId: string; campaignName: string | null; adsetId: string | null; adsetName: string | null } | null;
  dmStatus: "SENT" | "FAILED" | "SKIPPED";
  dmError: string | null;
  link: string;
  content: { id: string; status: string; title: string | null; thumbnailUrl: string | null } | null;
  draftAd: { id: string; status: string; metaAdId: string | null } | null;
  expiresAt: string;
  acceptedAt: string | null;
  submittedAt: string | null;
  createdAt: string;
}

export interface CreateTikTokAdRequestInput {
  sampleContentId: string;
  programId?: string;
  newProgram?: { title: string; commissionRate: number };
  flatFee?: number | null;
  commissionRate?: number | null;
  checklist?: TikTokAdRequestChecklistItem[];
  notes?: string;
  targetCampaignId?: string | null;
  targetCampaignName?: string | null;
  targetAdsetId?: string | null;
  targetAdsetName?: string | null;
}

export async function listTikTokAdRequests(status?: TikTokAdRequestStatus): Promise<TikTokAdRequest[]> {
  const { data } = await brandApi.get<TikTokAdRequest[]>("/tiktok-shop/ad-requests", { params: status ? { status } : undefined });
  return data;
}

export async function createTikTokAdRequest(input: CreateTikTokAdRequestInput): Promise<TikTokAdRequest> {
  const { data } = await brandApi.post<TikTokAdRequest>("/tiktok-shop/ad-requests", input);
  return data;
}

export async function resendTikTokAdRequest(id: string): Promise<TikTokAdRequest> {
  const { data } = await brandApi.post<TikTokAdRequest>(`/tiktok-shop/ad-requests/${id}/resend`);
  return data;
}

export async function cancelTikTokAdRequest(id: string): Promise<TikTokAdRequest> {
  const { data } = await brandApi.delete<TikTokAdRequest>(`/tiktok-shop/ad-requests/${id}`);
  return data;
}
